import type { 
  BallState, 
  PaddleState, 
  Position, 
  Size, 
  GameConfig, 
  CollisionResult,
  GameState
} from '../types/game';

// Physics constants
const PHYSICS = {
  BALL_BOUNCE_ANGLE_FACTOR: 0.25,
  BALL_SPEED_MULTIPLIER: 1.1,
  PADDLE_COLLISION_THRESHOLD: 0.2,
  WALL_BOUNCE_DAMPING: 1.0
} as const;

/**
 * Check if two rectangles are colliding using AABB collision detection
 */
export function checkAABBCollision(
  rect1: { x: number; y: number; width: number; height: number },
  rect2: { x: number; y: number; width: number; height: number }
): boolean {
  return rect1.x < rect2.x + rect2.width &&
         rect1.x + rect1.width > rect2.x &&
         rect1.y < rect2.y + rect2.height &&
         rect1.y + rect1.height > rect2.y;
}

/**
 * Calculate collision between ball and paddle
 */
export function calculateBallPaddleCollision(
  ball: BallState,
  paddle: PaddleState
): CollisionResult {
  const ballRect = {
    x: ball.position.x,
    y: ball.position.y,
    width: ball.size,
    height: ball.size
  };

  const paddleRect = {
    x: paddle.position.x,
    y: paddle.position.y,
    width: paddle.size.width,
    height: paddle.size.height
  };

  const collided = checkAABBCollision(ballRect, paddleRect);

  if (!collided) {
    return { collided: false };
  }

  // Calculate collision normal (simplified)
  const ballCenterX = ball.position.x + ball.size / 2;
  const ballCenterY = ball.position.y + ball.size / 2;
  const paddleCenterX = paddle.position.x + paddle.size.width / 2;
  const paddleCenterY = paddle.position.y + paddle.size.height / 2;

  const normal: Position = {
    x: ballCenterX - paddleCenterX,
    y: ballCenterY - paddleCenterY
  };

  // Normalize
  const length = Math.sqrt(normal.x * normal.x + normal.y * normal.y);
  if (length > 0) {
    normal.x /= length;
    normal.y /= length;
  }

  return { collided: true, normal };
}

/**
 * Update ball position and handle collisions
 */
export function updateBallPhysics(
  ball: BallState,
  playerPaddle: PaddleState,
  aiPaddle: PaddleState,
  canvasSize: Size
): { ball: BallState; scored: 'player' | 'ai' | null } {
  const newBall = { ...ball };
  
  // Update position
  newBall.position.x += newBall.velocity.x;
  newBall.position.y += newBall.velocity.y;

  // Check wall collisions (top and bottom)
  if (newBall.position.y <= 0 || newBall.position.y + newBall.size >= canvasSize.height) {
    const offset = newBall.velocity.y < 0 
      ? 0 - newBall.position.y 
      : canvasSize.height - (newBall.position.y + newBall.size);
    
    newBall.position.y += 2 * offset;
    newBall.velocity.y *= -PHYSICS.WALL_BOUNCE_DAMPING;
  }

  // Check paddle collisions
  const activePlayer = newBall.velocity.x < 0 ? playerPaddle : aiPaddle;
  const paddleCollision = calculateBallPaddleCollision(newBall, activePlayer);

  if (paddleCollision.collided) {
    const direction = newBall.velocity.x < 0 ? 1 : -1;
    const hitPosition = (newBall.position.y + newBall.size - activePlayer.position.y) / 
                       (activePlayer.size.height + newBall.size);
    
    const yDirection = (hitPosition > 0.5 ? -1 : 1) * direction;
    const angle = PHYSICS.BALL_BOUNCE_ANGLE_FACTOR * Math.PI * (2 * hitPosition + direction) + 
                  Math.random() * 0.1;
    
    const speedMultiplier = Math.abs(angle) > PHYSICS.PADDLE_COLLISION_THRESHOLD * Math.PI 
      ? PHYSICS.BALL_SPEED_MULTIPLIER 
      : 1;

    // Position ball outside paddle
    newBall.position.x = activePlayer === playerPaddle 
      ? playerPaddle.position.x + playerPaddle.size.width
      : aiPaddle.position.x - newBall.size;

    // Update velocity
    newBall.velocity.x = speedMultiplier * -newBall.velocity.x;
    newBall.velocity.y = speedMultiplier * yDirection * newBall.velocity.x * Math.sin(angle);
  }

  // Check scoring
  let scored: 'player' | 'ai' | null = null;
  if (newBall.position.x + newBall.size < 0) {
    scored = 'ai';
  } else if (newBall.position.x > canvasSize.width) {
    scored = 'player';
  }

  return { ball: newBall, scored };
}

/**
 * Update player paddle position based on input
 */
export function updatePlayerPaddle(
  paddle: PaddleState,
  upPressed: boolean,
  downPressed: boolean,
  canvasHeight: number
): PaddleState {
  const newPaddle = { ...paddle };
  
  if (upPressed) {
    newPaddle.position.y -= paddle.speed;
  }
  if (downPressed) {
    newPaddle.position.y += paddle.speed;
  }

  // Keep paddle within canvas bounds
  newPaddle.position.y = Math.max(0, Math.min(
    newPaddle.position.y, 
    canvasHeight - paddle.size.height
  ));

  return newPaddle;
}

/**
 * Update AI paddle position with configurable difficulty
 */
export function updateAIPaddle(
  paddle: PaddleState,
  ball: BallState,
  config: GameConfig,
  canvasHeight: number
): PaddleState {
  const newPaddle = { ...paddle };
  
  // Calculate target position (center of paddle should align with ball)
  let targetY = ball.position.y - (config.paddle.height - ball.size) * 0.5;
  
  // Add subtle variation for AI vs AI mode to make them feel different
  let trackingAccuracy = config.ai.difficulty;
  let smoothingFactor = 0.1;
  
  if (config.gameMode === 'ai-vs-ai') {
    // In AI vs AI mode, add some gentle personality to the right paddle
    const personalityNoise = (Math.random() - 0.5) * config.paddle.height * 0.5; // More noise - INCREASED
    const reactionVariation = 0.6 + (Math.random() * 0.7); // 0.6 to 1.3 multiplier - WIDER range
    
    targetY += personalityNoise;
    trackingAccuracy *= reactionVariation;
    smoothingFactor = 0.09; // Slightly different smoothing
  }
  
  const currentY = newPaddle.position.y;
  
  // Move toward target with smoothing
  const deltaY = targetY - currentY;
  const moveAmount = deltaY * trackingAccuracy * smoothingFactor;
  
  newPaddle.position.y += moveAmount;
  
  // Keep paddle within canvas bounds
  newPaddle.position.y = Math.max(0, Math.min(
    newPaddle.position.y, 
    canvasHeight - paddle.size.height
  ));

  return newPaddle;
}

/**
 * Update player paddle as AI (for AI vs AI mode) - Human-like behavior
 */
export function updatePlayerPaddleAsAI(
  paddle: PaddleState,
  ball: BallState,
  config: GameConfig,
  canvasHeight: number
): PaddleState {
  const newPaddle = { ...paddle };
  
  // HUMAN-LIKE BEHAVIOR: Predict where ball will be when it reaches paddle
  const paddleX = paddle.position.x + paddle.size.width / 2;
  const ballToPaddleDistance = Math.abs(ball.position.x - paddleX);
  
  // Only try to predict if ball is moving toward paddle
  const ballMovingTowardPaddle = (ball.velocity.x > 0 && ball.position.x < paddleX) || 
                                 (ball.velocity.x < 0 && ball.position.x > paddleX);
  
  let targetY;
  if (ballMovingTowardPaddle && Math.abs(ball.velocity.x) > 0.1) {
    // Predict ball position when it reaches paddle (simple linear prediction)
    const timeToReachPaddle = ballToPaddleDistance / Math.abs(ball.velocity.x);
    const predictedBallY = ball.position.y + (ball.velocity.y * timeToReachPaddle);
    
    // Target: center paddle on predicted ball position
    targetY = predictedBallY - (config.paddle.height / 2);
    
    // Add some human imperfection to prediction
    const predictionError = (Math.random() - 0.5) * config.paddle.height * 0.25;
    targetY += predictionError;
  } else {
    // If ball not moving toward us, just track current position loosely
    targetY = ball.position.y - (config.paddle.height / 2);
  }
  
  // HUMAN-LIKE DECISION MAKING: Only move if we're "far enough" from target
  const currentCenterY = paddle.position.y + config.paddle.height / 2;
  const targetCenterY = targetY + config.paddle.height / 2;
  const distanceToTarget = Math.abs(currentCenterY - targetCenterY);
  const movementThreshold = config.paddle.height * 0.25; // Must be 25% of paddle height away
  
  let moveAmount = 0;
  
  if (distanceToTarget > movementThreshold) {
    // HUMAN-LIKE MOVEMENT: Move at full speed toward target, then stop
    const direction = targetCenterY > currentCenterY ? 1 : -1;
    const fullSpeed = config.paddle.speed;
    
    // Add slight speed variation (human inconsistency)
    const speedVariation = 0.8 + (Math.random() * 0.4); // 0.8 to 1.2x speed
    moveAmount = direction * fullSpeed * speedVariation;
    
    // Occasional confusion (moves wrong way briefly)
    const confusionChance = 0.06; // 6% chance
    if (Math.random() < confusionChance) {
      moveAmount = -moveAmount * 0.7; // Move wrong way at reduced speed
    }
  }
  // If close enough to target, don't move (like releasing the key)
  
  newPaddle.position.y += moveAmount;
  
  // Keep paddle within canvas bounds
  newPaddle.position.y = Math.max(0, Math.min(
    newPaddle.position.y, 
    canvasHeight - paddle.size.height
  ));

  return newPaddle;
}

/**
 * Create initial ball state for serving
 */
export function createServeBall(
  config: GameConfig,
  servingSide: 'player' | 'ai',
  playerPaddle: PaddleState,
  aiPaddle: PaddleState
): BallState {
  const random = Math.random();
  const angle = 0.1 * Math.PI * (1 - 2 * random);
  const side = servingSide === 'player' ? 1 : -1;
  
  return {
    position: {
      x: servingSide === 'player' 
        ? playerPaddle.position.x + playerPaddle.size.width
        : aiPaddle.position.x - config.ball.size,
      y: (config.canvas.height - config.ball.size) * random
    },
    velocity: {
      x: config.ball.speed * Math.cos(angle) * side,
      y: config.ball.speed * Math.sin(angle)
    },
    size: config.ball.size,
    speed: config.ball.speed
  };
}

/**
 * Create default game configuration
 */
export function createDefaultConfig(): GameConfig {
  return {
    canvas: {
      width: 700,
      height: 600
    },
    ball: {
      size: 10,
      speed: 2
    },
    paddle: {
      width: 20,
      height: 100,
      speed: 5
    },
    controls: {
      upKey: 'ArrowUp',
      downKey: 'ArrowDown'
    },
    ai: {
      difficulty: 0.8,
      reactionTime: 100
    },
    ai2: {
      difficulty: 0.75,
      reactionTime: 120
    },
    gameMode: 'player-vs-ai',
    visual: {
      colors: {
        background: '#000000',           // Black background
        playerPaddle: '#4caf50',         // Green for player (left)
        aiPaddle: '#2196f3',             // Blue for AI (right) 
        ball: '#ffffff',                 // White ball
        centerLine: '#ffffff',           // White center line
        text: '#ffffff'                  // White text
      },
      ui: {
        showOverlayText: true,           // Show "Press SPACE to start" etc
        showCenterLine: true,            // Show the center line
        font: 'Arial',                   // Font family
        fontSize: 30                     // Font size for overlay text
      }
    }
  };
}

/**
 * Create initial game state
 */
export function createInitialGameState(config: GameConfig): GameState {
  const playerPaddle: PaddleState = {
    position: { x: 10, y: config.canvas.height / 2 - config.paddle.height / 2 },
    size: { width: config.paddle.width, height: config.paddle.height },
    speed: config.paddle.speed
  };

  const aiPaddle: PaddleState = {
    position: { 
      x: config.canvas.width - config.paddle.width - 10, 
      y: config.canvas.height / 2 - config.paddle.height / 2 
    },
    size: { width: config.paddle.width, height: config.paddle.height },
    speed: config.paddle.speed
  };

  return {
    ball: createServeBall(config, 'player', playerPaddle, aiPaddle),
    playerPaddle,
    aiPaddle,
    score: { player: 0, ai: 0 },
    isPlaying: false,
    isPaused: false,
    gamePhase: 'waiting',
    winner: null,
    gameMode: config.gameMode
  };
} 